import type { LucideIcon } from 'lucide-react'
import { AnalyticsEvents } from '@/lib/analytics/events'
import { House, Activity, Utensils, BookmarkCheck, BadgeQuestionMark, Menu, History } from 'lucide-react'

export interface NavItem {
  to: string
  icon: LucideIcon
  label: string
  /** Extra analytics event fired on click, in addition to the nav-click event. */
  analyticsEvent?: string
}

/**
 * Single source of truth for the primary content nav destinations, shared by
 * the mobile bottom tab bar (`MainNavigationTabBar`) and the desktop left
 * rail (`DesktopNavRail`) — D45b Phase 2 (round-1 fix N11: no duplicated
 * list or duplicated active-state predicate between the two surfaces).
 *
 * The bar renders all six items. The rail renders only `railNavItems`
 * (Menu excluded, desktop-only extras such as History added) — on desktop,
 * Menu is reached via the rail's `⋯` overlay trigger instead of a nav row.
 * See DesktopNavRail.tsx.
 */
export const navItems: NavItem[] = [
  { to: '/dashboard', icon: House, label: 'Dashboard' },
  { to: '/feed', icon: Activity, label: 'Feed' },
  { to: '/search', icon: Utensils, label: 'Restaurants' },
  { to: '/lists', icon: BookmarkCheck, label: 'Lists' },
  { to: '/quizzes', icon: BadgeQuestionMark, label: 'Quizzes' },
  { to: '/menu', icon: Menu, label: 'Menu' },
]

/**
 * Destinations that get their own row on the desktop rail ONLY. On phones the
 * Menu overlay is the sole entry point for these (the six-slot tab bar has no
 * room), so `useMenuItems` hides the matching Menu row on desktop to avoid a
 * duplicate entry point — see `railDestination` in `pages/menu/Menu.tsx`.
 */
export const desktopOnlyNavItems: NavItem[] = [
  { to: '/history', icon: History, label: 'History', analyticsEvent: AnalyticsEvents.HISTORY_CLICKED },
]

/** Content items (no Menu row) plus desktop-only extras — used by the desktop rail. */
export const railNavItems: NavItem[] = [
  ...navItems.filter((item) => item.to !== '/menu'),
  ...desktopOnlyNavItems,
]

/**
 * Shared active-state predicate. A nav item is active when the current
 * pathname is an exact match or a sub-path of its target route.
 */
export function isNavItemActive(to: string, pathname: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`)
}
